import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  ClickAwayListener,
  Fade,
  IconButton,
  useTheme,
  useMediaQuery,
  Collapse,
  Dialog,
  Slide,
  Container,
} from "@mui/material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { TransitionProps } from "@mui/material/transitions";
import { usePageSearch } from "@/hooks/search/usePageSearch";
import { useFreeloBoardsSearchProvider } from "@/hooks/search/useFreeloBoardsSearchProvider";
import { useClientsSearchProvider } from "@/hooks/search/useClientsSearchProvider";

const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

interface PageSearchBarProps {
  onPageSelect?: () => void;
}

export const PageSearchBar: React.FC<PageSearchBarProps> = ({
  onPageSelect
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const freeloSearchProvider = useFreeloBoardsSearchProvider();
  const clientsSearchProvider = useClientsSearchProvider();
  const { searchQuery, setSearchQuery, searchResults, clearSearch } = usePageSearch({
    providers: [freeloSearchProvider, clientsSearchProvider]
  });
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [isDismissed, setIsDismissed] = useState(false);
  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchQuery.length > 0 && searchResults.length > 0 && !isDismissed) {
      setShowResults(true);
    } else if (searchQuery.length === 0) {
      setShowResults(false);
      setIsDismissed(false);
    }
    setSelectedIndex(-1);
  }, [searchQuery, searchResults, isDismissed]);

  useEffect(() => {
    setIsExpanded(!isMobile);
  }, [isMobile]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setIsDismissed(false);
  };

  const handlePageClick = (path: string) => {
    navigate(path);
    clearSearch();
    setShowResults(false);
    setMobileDialogOpen(false);
    onPageSelect?.();
  };

  const handleClickAway = () => {
    setShowResults(false);
    setSelectedIndex(-1);
    setIsDismissed(true);
    if (isMobile && !searchQuery) {
      setIsExpanded(false);
    }
  };

  const handleToggleSearch = () => {
    if (isMobile) {
      setMobileDialogOpen(true);
      setIsDismissed(false);
      setTimeout(() => {
        mobileSearchRef.current?.focus();
      }, 100);
    }
  };

  const handleCloseMobileDialog = () => {
    setMobileDialogOpen(false);
    clearSearch();
    setShowResults(false);
    setIsDismissed(false);
  };

  const handleClearSearch = () => {
    clearSearch();
    setShowResults(false);
    setSelectedIndex(-1);
    setIsDismissed(false);
    if (isMobile) {
      setMobileDialogOpen(false);
    }
  };

  const handleMobileSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setIsDismissed(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!showResults || searchResults.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case "Enter":
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handlePageClick(searchResults[selectedIndex].path);
        }
        break;
      case "Escape":
        setShowResults(false);
        setSelectedIndex(-1);
        break;
    }
  };

  if (isMobile) {
    return (
      <>
        <IconButton
          onClick={handleToggleSearch}
          sx={{ 
            color: theme.palette.mode === 'light' ? "rgba(0, 0, 0, 0.87)" : "white",
            "&:hover": {
              backgroundColor: theme.palette.mode === 'light' 
                ? "rgba(0, 0, 0, 0.1)" 
                : "rgba(255, 255, 255, 0.1)"
            }
          }}
        >
          <SearchIcon />
        </IconButton>

        <Dialog
          fullScreen
          open={mobileDialogOpen}
          onClose={handleCloseMobileDialog}
          TransitionComponent={SlideTransition}
          sx={{
            '& .MuiDialog-paper': {
              background: theme.palette.mode === 'light' 
                ? 'rgba(255, 255, 255, 0.95)' 
                : 'rgba(18, 18, 18, 0.95)',
              backdropFilter: 'blur(20px)',
            }
          }}
        >
          <Container maxWidth="md" sx={{ pt: 8, pb: 4 }}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconButton 
                  onClick={handleCloseMobileDialog}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <ClearIcon />
                </IconButton>
                <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
                  Search
                </Typography>
              </Box>
              
              <TextField
                inputRef={mobileSearchRef}
                fullWidth
                size="medium"
                placeholder="Search pages, projects, clients..."
                value={searchQuery}
                onChange={handleMobileSearchChange}
                onKeyDown={handleKeyDown}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClearSearch}
                        edge="end"
                        sx={{ color: 'text.secondary' }}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '1.2rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    backgroundColor: 'transparent',
                    borderRadius: 0,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    '&.Mui-focused': {
                      borderBottomColor: theme.palette.primary.main,
                    }
                  }
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '1.2rem',
                    py: 2,
                  }
                }}
              />
            </Box>

            {searchResults.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  backgroundColor: theme.palette.mode === 'light' 
                    ? 'rgba(255, 255, 255, 0.8)' 
                    : 'rgba(30, 30, 30, 0.8)',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                }}
              >
                <List sx={{ p: 0 }}>
                  {searchResults.map((page, index) => (
                    <React.Fragment key={page.path}>
                      <ListItem
                        component="div"
                        onClick={() => handlePageClick(page.path)}
                        sx={{
                          cursor: "pointer",
                          py: 2,
                          px: 3,
                          backgroundColor: selectedIndex === index ? "action.selected" : "transparent",
                          "&:hover": {
                            backgroundColor: selectedIndex === index ? "action.selected" : "action.hover",
                          },
                        }}
                      >
                        {page.icon && (
                          <ListItemIcon sx={{ minWidth: 48, fontSize: 24 }}>
                            {page.icon}
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={page.title}
                          secondary={page.description || page.category || page.segment}
                          primaryTypographyProps={{
                            variant: "body1",
                            fontWeight: 600,
                          }}
                          secondaryTypographyProps={{
                            variant: "body2",
                            color: "text.secondary",
                          }}
                        />
                      </ListItem>
                      {index < searchResults.length - 1 && (
                        <Divider />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}

            {searchQuery && searchResults.length === 0 && (
              <Box sx={{ 
                textAlign: "center", 
                py: 8,
                px: 2,
              }}>
                <SearchIcon sx={{ 
                  fontSize: 64, 
                  color: 'text.disabled',
                  mb: 2,
                }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No results found
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Try searching for something else
                </Typography>
              </Box>
            )}
          </Container>
        </Dialog>
      </>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box 
        sx={{ 
          position: "relative", 
          display: "flex", 
          alignItems: "center",
          justifyContent: "center",
          width: "100%", 
          maxWidth: 800,
        }} 
        ref={searchRef}
      >
        <TextField
          size="small"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsDismissed(false);
            if (searchQuery.length > 0 && searchResults.length > 0) {
              setShowResults(true);
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  edge="end"
                  sx={{ mr: -1 }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              backgroundColor: theme.palette.mode === "dark" ? "rgba(30, 30, 30, 0.9)" : "rgba(255, 255, 255, 0.9)",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.divider,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.text.secondary,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
              "& input": {
                color: "text.primary",
              },
              "& input::placeholder": {
                color: "text.secondary",
                opacity: 1,
              },
            },
          }}
          sx={{ 
            width: "100%",
            maxWidth: "800px"
          }}
        />

        <Fade in={showResults}>
          <Paper
            elevation={8}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1300,
              mt: 1,
              maxHeight: 400,
              overflow: "auto",
            }}
          >
            {searchResults.length > 0 ? (
              <List dense>
                {searchResults.map((page, index) => (
                  <React.Fragment key={page.path}>
                    <ListItem
                      component="div"
                      onClick={() => handlePageClick(page.path)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: selectedIndex === index ? "action.selected" : "transparent",
                        "&:hover": {
                          backgroundColor: selectedIndex === index ? "action.selected" : "action.hover",
                        },
                      }}
                    >
                      {page.icon && (
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {page.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={page.title}
                        secondary={page.description || page.category || page.segment}
                        primaryTypographyProps={{
                          variant: "body2",
                          fontWeight: 500,
                        }}
                        secondaryTypographyProps={{
                          variant: "caption",
                          color: "text.secondary",
                        }}
                      />
                    </ListItem>
                    {index < searchResults.length - 1 && (
                      <Divider variant="inset" component="li" />
                    )}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              searchQuery && (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No pages found matching "{searchQuery}"
                  </Typography>
                </Box>
              )
            )}
          </Paper>
        </Fade>
      </Box>
    </ClickAwayListener>
  );
}; 